import { useEffect, useRef, useState } from 'react';
import {
  Ban,
  CalendarX,
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Clock,
  PenLine,
  Plus,
  RotateCcw,
  Send,
  Sparkles,
  Ticket,
  X,
} from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import { useToast } from '@/components/ux/Toast';
import { useConfirm } from '@/components/ConfirmModal';
import {
  useRejectTriageDraft,
  useSendTriageDraft,
  useSendTriageReply,
  useTriageAssist,
} from '../../api/useAdminTriage';
import { timeAgo } from '../ops/opsUi';

// The agent's suggested resolutions for a ticket: one card per option, each a
// reply plus the actions that make it true (refund, free ticket, ...). Cards
// start collapsed to a one-line preview; pressing one opens its reply for
// editing, lets the admin trim or add actions, and "Do it & send" runs the
// actions and then sends. The server stops before the email if any action
// fails, and a retry skips the ones that already ran.
//
// Two things are always there, suggestion or not: a "Write your own reply"
// card (same editor, same actions), and an "Ask AI" box that turns an
// instruction ("reply in French", "apologise and add a free ticket") into one
// more option, working from whatever reply is currently open.

type Action = AdminAPI.TriageAction;
type Account = AdminAPI.TriageAccountContext | null;
type Working = { body: string; actions: Action[] };

const COMPOSE = 'compose';

function money(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

function shortDate(value: string): string {
  const d = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const actionKey = (a: Action) => JSON.stringify(a);

// Human label for an action, resolved against the user's account; `problem`
// is set when the action cannot run as things stand.
function describeAction(action: Action, account: Account): { label: string; problem: string | null } {
  switch (action.type) {
    case 'grant_ticket':
      return { label: `Grant ${action.count} free ticket${action.count === 1 ? '' : 's'}`, problem: null };
    case 'extend_subscription': {
      const sub = account?.stripeSubscription;
      return {
        label: `Extend subscription ${action.days} days${sub?.renewsAt ? ` (next charge was ${shortDate(sub.renewsAt)})` : ''}`,
        problem: sub ? null : 'No Stripe subscription',
      };
    }
    case 'cancel_subscription': {
      const sub = account?.stripeSubscription;
      const label =
        action.when === 'now'
          ? 'Cancel subscription now'
          : `Stop renewal${sub?.renewsAt ? ` (ends ${shortDate(sub.renewsAt)})` : ''}`;
      return {
        label,
        problem: sub
          ? null
          : account?.nativeSubscription
            ? 'App/Play Store subscription: the member cancels it'
            : 'No active subscription',
      };
    }
    case 'refund_payment': {
      const payment = action.paymentId
        ? account?.recentPayments.find((p) => p.id === action.paymentId)
        : account?.recentPayments.find((p) => p.refundable);
      if (!payment) return { label: 'Refund latest payment', problem: 'Nothing refundable' };
      const amount = action.amount ?? payment.amount - payment.refundedAmount;
      return {
        label: `Refund ${money(amount, payment.currency)} of the ${shortDate(payment.createdAt)} payment`,
        problem: payment.refundable ? null : 'Not refundable from here',
      };
    }
    case 'cancel_booking': {
      const booking = account?.upcomingBookings.find((b) => b.bookingId === action.bookingId);
      const where = booking ? `${shortDate(booking.date)}${booking.neighborhoodName ? ` · ${booking.neighborhoodName}` : ''}` : 'booking';
      return {
        label: `Cancel ${where}${action.restoreTicket ? ', return ticket' : ''}`,
        problem: booking ? null : 'Not an upcoming booking',
      };
    }
  }
}

function ActionIcon({ type, className = 'w-3.5 h-3.5' }: { type: Action['type']; className?: string }) {
  if (type === 'grant_ticket') return <Ticket className={className} />;
  if (type === 'extend_subscription') return <Clock className={className} />;
  if (type === 'cancel_subscription') return <Ban className={className} />;
  if (type === 'refund_payment') return <RotateCcw className={className} />;
  return <CalendarX className={className} />;
}

// Presets offered by "Add action", limited to what this account supports.
function actionPresets(account: Account): Action[] {
  if (!account) return [];
  const presets: Action[] = [{ type: 'grant_ticket', count: 1 }];
  if (account.stripeSubscription) {
    presets.push(
      { type: 'extend_subscription', days: 7 },
      { type: 'cancel_subscription', when: 'period_end' },
      { type: 'cancel_subscription', when: 'now' }
    );
  }
  if (account.recentPayments.some((p) => p.refundable)) presets.push({ type: 'refund_payment' });
  for (const b of account.upcomingBookings) {
    presets.push({ type: 'cancel_booking', bookingId: b.bookingId, restoreTicket: b.usesTicket });
  }
  return presets;
}

function ActionChip({ action, account, result }: { action: Action; account: Account; result?: AdminAPI.TriageActionResult }) {
  const { label, problem } = describeAction(action, account);
  const tone = result?.ok
    ? 'border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-400'
    : problem || result
      ? 'border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400'
      : 'border-neutral-300 dark:border-neutral-600 text-dark-muted';
  return (
    <span
      title={result?.message ?? problem ?? undefined}
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[11px] ${tone}`}
    >
      {result?.ok ? <Check className="w-3 h-3" /> : <ActionIcon type={action.type} className="w-3 h-3" />}
      {label}
    </span>
  );
}

function ResolutionOption({
  draft,
  ticketId,
  account,
  userLinked,
  open,
  focusOnOpen,
  onToggle,
  onWorkingChange,
  onSent,
}: {
  // null = the admin's own reply: not saved until it is sent.
  draft: AdminAPI.TriageDraftInfo | null;
  ticketId: string;
  account: Account;
  userLinked: boolean;
  open: boolean;
  // False when the option opened by itself (lone option), so loading a
  // ticket does not steal keyboard focus.
  focusOnOpen: boolean;
  onToggle: () => void;
  // Reports the live (possibly unsaved) reply and actions, for "Ask AI".
  onWorkingChange: (working: Working) => void;
  onSent?: () => void;
}) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const sendDraft = useSendTriageDraft();
  const sendReply = useSendTriageReply();
  const rejectDraft = useRejectTriageDraft();
  const sending = sendDraft.isPending || sendReply.isPending;

  const [body, setBody] = useState(draft?.body ?? '');
  const [actions, setActions] = useState<Action[]>(draft?.actions ?? []);
  const [resolveAfterSend, setResolveAfterSend] = useState(true);
  const [showWhy, setShowWhy] = useState(false);
  const [addingAction, setAddingAction] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState('');

  useEffect(() => {
    onWorkingChange({ body, actions });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, actions]);

  const resultFor = (action: Action) =>
    [...(draft?.actionResults ?? [])].reverse().find((r) => actionKey(r.action) === actionKey(action));
  const pending = actions.filter((a) => !resultFor(a)?.ok);
  const blocked = pending.some((a) => describeAction(a, account).problem);
  const edited = body !== draft?.body;
  const label = draft ? (draft.label ?? 'Reply') : 'Write your own reply';
  const preview = (draft ? draft.body : body).replace(/\s+/g, ' ').trim();

  const handleSend = async () => {
    if (pending.length > 0) {
      const ok = await confirm({
        title: `${label}: run ${pending.length} action${pending.length === 1 ? '' : 's'} and send?`,
        message: `${pending.map((a) => describeAction(a, account).label).join('\n')}\n\nThen the reply is sent. If an action fails, nothing is sent.`,
        confirmText: 'Do it & send',
        type: pending.some((a) => a.type === 'refund_payment' || a.type === 'cancel_subscription') ? 'danger' : undefined,
      });
      if (!ok) return;
    }
    const callbacks = {
      onSuccess: () => {
        toast.success(actions.length > 0 ? 'Done and sent' : 'Reply sent');
        onSent?.();
      },
      onError: (err: unknown) => toast.error(err instanceof Error ? err.message : 'Send failed'),
    };
    if (draft) {
      sendDraft.mutate(
        { draftId: draft.id, ...(edited ? { body } : {}), resolve: resolveAfterSend, actions },
        callbacks
      );
    } else {
      sendReply.mutate({ ticketId, body, resolve: resolveAfterSend, actions }, callbacks);
    }
  };

  const handleReject = () => {
    if (!draft) return;
    rejectDraft.mutate(
      { draftId: draft.id, ...(rejectNote.trim() ? { note: rejectNote.trim() } : {}) },
      {
        onSuccess: () => toast.success('Option rejected'),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Reject failed'),
      }
    );
  };

  const presets = actionPresets(account).filter((p) => !actions.some((a) => actionKey(a) === actionKey(p)));

  return (
    <div
      className={`rounded-lg border ${
        open
          ? 'border-primary-300 dark:border-primary-700 bg-white dark:bg-neutral-900'
          : 'border-neutral-200 dark:border-neutral-700 bg-white/60 dark:bg-neutral-900/60 hover:border-primary-300'
      }`}
    >
      <button onClick={onToggle} className="w-full text-left px-3 py-2 space-y-1">
        <div className="flex items-center gap-2">
          {open ? (
            <ChevronDown className="w-3.5 h-3.5 text-dark-muted shrink-0" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5 text-dark-muted shrink-0" />
          )}
          {!draft && <PenLine className="w-3.5 h-3.5 text-dark-muted shrink-0" />}
          <span className="text-sm font-medium flex-1 min-w-0 truncate">{label}</span>
          {draft?.confidence != null && (
            <span className="text-[11px] text-dark-muted" title="Agent's estimate that this goes out unedited">
              {draft.confidence}%
            </span>
          )}
        </div>
        {!open && (draft || preview) && (
          <div className="pl-5 flex flex-wrap items-center gap-1">
            {(draft?.actions ?? actions).map((a) => (
              <ActionChip key={actionKey(a)} action={a} account={account} result={resultFor(a)} />
            ))}
            <span className="text-xs text-dark-muted truncate min-w-0 flex-1">{preview}</span>
          </div>
        )}
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          {draft?.rationale && (
            <div className="pl-5">
              <button
                onClick={() => setShowWhy(!showWhy)}
                className="text-[11px] text-dark-muted hover:text-primary-500 inline-flex items-center gap-0.5"
              >
                {showWhy ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Why this
              </button>
              {showWhy && <p className="mt-1 text-xs text-dark-muted whitespace-pre-wrap">{draft.rationale}</p>}
            </div>
          )}

          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={Math.min(14, Math.max(4, body.split('\n').length + 1))}
            autoFocus={focusOnOpen}
            placeholder={draft ? undefined : 'Write your reply…'}
            className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-sm leading-relaxed"
          />

          <div className="space-y-1">
            {actions.map((action) => {
              const { label: actionLabel, problem } = describeAction(action, account);
              const result = resultFor(action);
              return (
                <div key={actionKey(action)} className="flex items-center gap-2 text-xs">
                  {result?.ok ? (
                    <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  ) : problem || result ? (
                    <CircleAlert className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  ) : (
                    <ActionIcon type={action.type} />
                  )}
                  <span className="flex-1 min-w-0">
                    {actionLabel}
                    {(result || problem) && (
                      <span className={result?.ok ? 'text-emerald-600' : 'text-amber-600'}>
                        {' '}
                        · {result?.message ?? problem}
                      </span>
                    )}
                  </span>
                  {!result?.ok && (
                    <button
                      onClick={() => setActions(actions.filter((a) => a !== action))}
                      title="Remove this action"
                      className="text-dark-muted hover:text-red-500"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
            {userLinked && presets.length > 0 && (
              <div className="relative">
                <button
                  onClick={() => setAddingAction(!addingAction)}
                  className="inline-flex items-center gap-1 text-[11px] text-dark-muted hover:text-primary-500"
                >
                  <Plus className="w-3 h-3" />
                  Add action
                </button>
                {addingAction && (
                  <div className="absolute z-10 mt-1 w-72 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 shadow-lg py-1">
                    {presets.map((preset) => (
                      <button
                        key={actionKey(preset)}
                        onClick={() => {
                          setActions([...actions, preset]);
                          setAddingAction(false);
                        }}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-neutral-50 dark:hover:bg-neutral-800 inline-flex items-center gap-2"
                      >
                        <ActionIcon type={preset.type} />
                        {describeAction(preset, account).label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!userLinked && actions.length > 0 && (
              <p className="text-[11px] text-amber-600">Link the user (right column) before running actions.</p>
            )}
          </div>

          {rejecting ? (
            <div className="flex flex-wrap items-center gap-2">
              <input
                value={rejectNote}
                onChange={(e) => setRejectNote(e.target.value)}
                placeholder="What's wrong with it? Optional, one line: it teaches the playbook"
                className="flex-1 min-w-[220px] px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-xs"
                autoFocus
              />
              <button
                onClick={handleReject}
                disabled={rejectDraft.isPending}
                className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-50"
              >
                Reject option
              </button>
              <button
                onClick={() => setRejecting(false)}
                className="px-2 py-1.5 rounded-lg text-xs text-dark-muted hover:text-primary-500"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={handleSend}
                disabled={sending || !body.trim() || blocked || (!userLinked && pending.length > 0)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 disabled:opacity-50"
              >
                <Send className="w-3.5 h-3.5" />
                {sending ? 'Working…' : pending.length > 0 ? 'Do it & send' : 'Send'}
              </button>
              <label className="inline-flex items-center gap-1.5 text-xs text-dark-muted cursor-pointer">
                <input
                  type="checkbox"
                  checked={resolveAfterSend}
                  onChange={(e) => setResolveAfterSend(e.target.checked)}
                  className="rounded"
                />
                Resolve after
              </label>
              {draft && (
                <button
                  onClick={() => setRejecting(true)}
                  disabled={sending}
                  className="ml-auto inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs text-dark-muted hover:text-red-500 disabled:opacity-50"
                >
                  <X className="w-3.5 h-3.5" />
                  Reject
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// "Ask AI": an instruction in, one more resolution option out.
function AskAi({
  ticketId,
  getWorking,
  onCreated,
}: {
  ticketId: string;
  // The reply currently open (if any), so "translate this" has a "this".
  getWorking: () => Working | null;
  onCreated: (draftId: string) => void;
}) {
  const toast = useToast();
  const assist = useTriageAssist();
  const [instruction, setInstruction] = useState('');
  const ready = instruction.trim().length >= 3 && !assist.isPending;

  const run = () => {
    if (!ready) return;
    const working = getWorking();
    assist.mutate(
      {
        ticketId,
        instruction: instruction.trim(),
        ...(working?.body.trim() ? { currentBody: working.body } : {}),
        ...(working?.actions.length ? { currentActions: working.actions } : {}),
      },
      {
        onSuccess: (res) => {
          setInstruction('');
          if (res.data) onCreated(res.data.draftId);
          const dropped = res.data?.droppedActions ?? [];
          if (dropped.length > 0) toast.error(`Added, but left out: ${dropped.join('; ')}`);
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'The AI request failed'),
      }
    );
  };

  return (
    <div className="flex items-center gap-2">
      <Sparkles className={`w-3.5 h-3.5 text-primary-500 shrink-0 ${assist.isPending ? 'animate-pulse' : ''}`} />
      <input
        value={instruction}
        onChange={(e) => setInstruction(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') run();
        }}
        disabled={assist.isPending}
        placeholder="Ask AI: “reply in French”, “apologise and add a free ticket”, “make it shorter”"
        className="flex-1 min-w-0 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-xs disabled:opacity-60"
      />
      <button
        onClick={run}
        disabled={!ready}
        className="px-3 py-1.5 rounded-lg border border-primary-300 dark:border-primary-700 text-xs font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/30 disabled:opacity-50"
      >
        {assist.isPending ? 'Writing…' : 'Suggest'}
      </button>
    </div>
  );
}

export default function TriageResolutions({
  ticketId,
  drafts,
  threads,
  account,
  userLinked,
}: {
  ticketId: string;
  drafts: AdminAPI.TriageDraftInfo[];
  threads: AdminAPI.TriageThreadInfo[];
  account: Account;
  userLinked: boolean;
}) {
  // undefined = untouched: a lone option starts open, several start collapsed,
  // and with no suggestion at all the admin's own reply is open. An id that is
  // no longer among the drafts (superseded, or an AI option still loading)
  // falls back to the same default.
  const [chosenId, setChosenId] = useState<string | null | undefined>(undefined);
  const [composeKey, setComposeKey] = useState(0);
  const working = useRef<Record<string, Working>>({});

  const known = chosenId === null || chosenId === COMPOSE || drafts.some((d) => d.id === chosenId);
  const openId = known
    ? (chosenId as string | null)
    : drafts.length === 1
      ? drafts[0].id
      : drafts.length === 0
        ? COMPOSE
        : null;
  const toggle = (id: string) => setChosenId(openId === id ? null : id);
  const target = drafts.length > 0 ? threads.find((t) => t.id === drafts[0].targetThreadId) : undefined;

  return (
    <div className="rounded-xl border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/10 p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-primary-500" />
        <h3 className="text-sm font-semibold">
          {drafts.length === 0
            ? 'Reply'
            : drafts.length === 1
              ? 'Suggested resolution'
              : `${drafts.length} suggested resolutions`}
        </h3>
        {drafts.length > 0 && <span className="text-[11px] text-dark-muted">{timeAgo(drafts[0].createdAt)}</span>}
        {target && threads.length > 1 && (
          <span className="ml-auto text-[11px] text-dark-muted truncate max-w-[45%]" title={target.subject ?? undefined}>
            replies to: {target.subject ?? '(no subject)'}
          </span>
        )}
      </div>
      {drafts.map((draft) => (
        // Keyed by id so edit state resets when a new proposal supersedes this one.
        <ResolutionOption
          key={draft.id}
          draft={draft}
          ticketId={ticketId}
          account={account}
          userLinked={userLinked}
          open={openId === draft.id}
          focusOnOpen={known}
          onToggle={() => toggle(draft.id)}
          onWorkingChange={(w) => {
            working.current[draft.id] = w;
          }}
        />
      ))}
      <ResolutionOption
        key={`${COMPOSE}-${composeKey}`}
        draft={null}
        ticketId={ticketId}
        account={account}
        userLinked={userLinked}
        open={openId === COMPOSE}
        focusOnOpen={known}
        onToggle={() => toggle(COMPOSE)}
        onWorkingChange={(w) => {
          working.current[COMPOSE] = w;
        }}
        onSent={() => {
          // Fresh, empty composer for the next reply.
          setComposeKey((k) => k + 1);
          setChosenId(undefined);
        }}
      />
      <AskAi
        ticketId={ticketId}
        getWorking={() => (openId ? (working.current[openId] ?? null) : null)}
        onCreated={(draftId) => setChosenId(draftId)}
      />
    </div>
  );
}
